import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Image as ImageIcon, File as FileIcon, Upload, Download, Eye, Trash2,
  Search, ArrowUpDown, X, Loader2, ExternalLink,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import SmartUploadDialog from "../../components/SmartUploadDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const CATEGORY_OPTIONS = [
  { value: "document",    label: "Documento" },
  { value: "contract",    label: "Contrato" },
  { value: "invoice",     label: "Fatura" },
  { value: "letter",      label: "Carta" },
  { value: "certificate", label: "Certificado" },
  { value: "other",       label: "Outros" },
];

const SORT_OPTIONS = [
  { value: "recent",     label: "Mais recentes" },
  { value: "oldest",     label: "Mais antigos" },
  { value: "name_asc",   label: "Nome (A → Z)" },
  { value: "name_desc",  label: "Nome (Z → A)" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const fmtSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const iconFor = (ft) => (ft === "pdf" ? FileText : ft === "image" ? ImageIcon : FileIcon);
const iconTone = (ft) =>
  ft === "pdf" ? "bg-rose-50 text-rose-600 border-rose-200"
  : ft === "image" ? "bg-sky-50 text-sky-600 border-sky-200"
  : "bg-slate-50 text-slate-600 border-slate-200";

// -------------------- MAIN PAGE --------------------
export default function ProcessesDocuments() {
  const [processes, setProcesses] = useState([]);
  const [selectedPid, setSelectedPid] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [fileType, setFileType] = useState("");
  const [sort, setSort] = useState("recent");
  const [smartOpen, setSmartOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);

  // Load processes for the picker
  useEffect(() => {
    let alive = true;
    api.get("/processes?page_size=500").then(({ data }) => {
      if (!alive) return;
      const items = data.items || [];
      setProcesses(items);
      if (items.length && !selectedPid) setSelectedPid(items[0].id);
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDocs = async (pid) => {
    if (!pid) { setDocs([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/processes/${pid}/documents`);
      setDocs(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocs(selectedPid); }, [selectedPid]);

  const selectedProc = useMemo(
    () => processes.find((p) => p.id === selectedPid),
    [processes, selectedPid]
  );

  // Client-side filtering / sorting (same signals as the portal).
  const filteredDocs = useMemo(() => {
    let list = docs;
    if (category) list = list.filter((d) => d.category === category);
    if (fileType) list = list.filter((d) => d.file_type === fileType);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((d) =>
        (d.name || "").toLowerCase().includes(s) ||
        (d.description || "").toLowerCase().includes(s) ||
        (d.receipt_number || "").toLowerCase().includes(s)
      );
    }
    const cmpDate = (a, b) => (a.created_at || "").localeCompare(b.created_at || "");
    const cmpName = (a, b) => (a.name || "").localeCompare(b.name || "");
    const sorters = {
      recent:    (a, b) => cmpDate(b, a),
      oldest:    cmpDate,
      name_asc:  cmpName,
      name_desc: (a, b) => cmpName(b, a),
    };
    return [...list].sort(sorters[sort] || sorters.recent);
  }, [docs, q, category, fileType, sort]);

  const categoriesInList = useMemo(() => {
    const set = new Set(docs.map((d) => d.category));
    return CATEGORY_OPTIONS.filter((o) => set.has(o.value));
  }, [docs]);

  const hasFilters = q || category || fileType || sort !== "recent";
  const clearAll = () => { setQ(""); setCategory(""); setFileType(""); setSort("recent"); };

  const handleUploaded = () => {
    loadDocs(selectedPid);
  };

  const confirmDelete = async () => {
    if (!deleteDoc) return;
    try {
      await api.delete(`/processes/${selectedPid}/documents/${deleteDoc.id}`);
      toast.success("Documento excluído");
      setDeleteDoc(null);
      loadDocs(selectedPid);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="processes-page-documents" className="space-y-5">
      {/* Header + process picker */}
      <div className="rounded-2xl border border-border bg-white p-5 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <Label htmlFor="proc-doc-picker" className="text-xs uppercase tracking-widest text-muted-foreground">
            Processo
          </Label>
          <select
            id="proc-doc-picker"
            data-testid="proc-doc-picker"
            value={selectedPid}
            onChange={(e) => setSelectedPid(e.target.value)}
            className="mt-1 block w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {processes.length === 0 && <option value="">Nenhum processo cadastrado</option>}
            {processes.map((p) => {
              const label = `${p.process_number} · ${p.client_name || p.service_name || "—"}`;
              return <option key={p.id} value={p.id}>{label}</option>;
            })}
          </select>
          {selectedProc && (
            <p className="mt-1 text-xs text-muted-foreground">
              Serviço: {selectedProc.service_name || "—"} · Responsável: {selectedProc.responsible_name || "—"}
            </p>
          )}
        </div>
        <Button
          type="button"
          onClick={() => setSmartOpen(true)}
          data-testid="proc-doc-smart-upload-open"
        >
          <Upload className="h-4 w-4 mr-1" /> Anexar documento
        </Button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-border bg-white p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="proc-doc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome ou descrição…"
            className="pl-9"
          />
        </div>
        <select
          data-testid="proc-doc-filter-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">Todas as categorias</option>
          {categoriesInList.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select
          data-testid="proc-doc-filter-file-type"
          value={fileType}
          onChange={(e) => setFileType(e.target.value)}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">Todos os tipos</option>
          <option value="pdf">PDF</option>
          <option value="image">Imagem</option>
          <option value="other">Outro</option>
        </select>
        <div className="relative">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <select
            data-testid="proc-doc-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent pl-8 pr-3 text-sm"
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {hasFilters && (
          <Button type="button" variant="ghost" size="sm" onClick={clearAll} data-testid="proc-doc-clear-filters">
            <X className="h-4 w-4 mr-1" /> Limpar
          </Button>
        )}
        <Badge variant="outline" className="ml-auto" data-testid="proc-doc-count">
          {loading ? "…" : `${filteredDocs.length} de ${docs.length}`}
        </Badge>
      </div>

      {/* List */}
      {loading && (
        <div className="rounded-2xl border border-dashed border-border bg-white px-10 py-14 text-center text-sm text-muted-foreground">
          Carregando documentos…
        </div>
      )}
      {!loading && !selectedPid && (
        <div className="rounded-2xl border border-dashed border-border bg-white px-10 py-14 text-center text-sm text-muted-foreground">
          Selecione um processo para visualizar seus documentos.
        </div>
      )}
      {!loading && selectedPid && filteredDocs.length === 0 && (
        <div
          data-testid="proc-doc-empty"
          className="rounded-2xl border border-dashed border-border bg-white px-10 py-14 text-center"
        >
          <FileIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {docs.length === 0
              ? "Este processo ainda não possui documentos anexados."
              : "Nenhum documento encontrado com esses filtros."}
          </p>
        </div>
      )}
      {!loading && filteredDocs.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid="proc-doc-list">
          {filteredDocs.map((d) => (
            <ProcessDocRow
              key={d.id}
              doc={d}
              onView={() => setViewerDoc(d)}
              onDelete={() => setDeleteDoc(d)}
              pid={selectedPid}
            />
          ))}
        </div>
      )}

      <SmartUploadDialog
        open={smartOpen}
        onOpenChange={setSmartOpen}
        onUploaded={handleUploaded}
      />
      <ViewerDialog
        pid={selectedPid}
        doc={viewerDoc}
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
      />
      <DeleteDialog
        doc={deleteDoc}
        open={!!deleteDoc}
        onCancel={() => setDeleteDoc(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

// -------------------- ROW --------------------
function ProcessDocRow({ doc, onView, onDelete, pid }) {
  const Icon = iconFor(doc.file_type);
  const [dling, setDling] = useState(false);
  const download = async () => {
    if (dling) return;
    setDling(true);
    const t0 = performance.now();
    try {
      const res = await api.get(`/processes/${pid}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name || `${doc.id}.bin`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      const ms = Math.round(performance.now() - t0);
      // AP-175 · dev-friendly perf timing surfaced to the user.
      toast.success(`Download iniciado (${ms}ms)`);
    } catch (e) {
      toast.error(formatApiError(e) || "Falha ao baixar o documento. Tente novamente.");
    } finally {
      setDling(false);
    }
  };
  return (
    <div
      data-testid={`proc-doc-row-${doc.id}`}
      className="rounded-2xl border border-border bg-white p-4 flex items-start gap-3 transition hover:border-primary/40 hover:shadow-sm"
    >
      <div className={`h-11 w-11 shrink-0 rounded-lg border grid place-items-center ${iconTone(doc.file_type)}`}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-foreground truncate" title={doc.name}>{doc.name}</p>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{doc.category_label}</Badge>
        </div>
        {doc.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2" title={doc.description}>
            {doc.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
          <span>{fmtDate(doc.created_at)}</span>
          {doc.size ? <span>· {fmtSize(doc.size)}</span> : null}
          <span className="uppercase font-mono text-[10px]">{doc.file_type}</span>
          {doc.uploaded_by_name && <span className="truncate">· por {doc.uploaded_by_name}</span>}
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2 shrink-0">
        <Button type="button" variant="outline" size="sm" onClick={onView} data-testid={`proc-doc-view-${doc.id}`}>
          <Eye className="h-4 w-4 mr-1" /> Visualizar
        </Button>
        <Button type="button" size="sm" onClick={download} disabled={dling} data-testid={`proc-doc-download-${doc.id}`}>
          {dling
            ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Baixando…</>
            : <><Download className="h-4 w-4 mr-1" /> Baixar</>}
        </Button>
        <Button type="button" variant="destructive" size="sm" onClick={onDelete} data-testid={`proc-doc-delete-${doc.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// -------------------- VIEWER DIALOG --------------------
function ViewerDialog({ pid, doc, open, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !doc || !pid) return;
    let alive = true;
    let currentUrl = "";
    setLoading(true); setError(""); setBlobUrl("");
    api.get(`/processes/${pid}/documents/${doc.id}/file`, { responseType: "blob" })
      .then(({ data }) => {
        if (!alive) return;
        currentUrl = URL.createObjectURL(data);
        setBlobUrl(currentUrl);
      })
      .catch((e) => alive && setError(formatApiError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [open, doc, pid]);

  if (!doc) return null;
  const isPdf = doc.file_type === "pdf";
  const isImg = doc.file_type === "image";

  const openInNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  const download = async () => {
    try {
      const res = await api.get(`/processes/${pid}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.name || `${doc.id}.bin`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden" data-testid="proc-doc-viewer">
        <DialogHeader className="px-5 py-3 border-b border-border">
          <DialogTitle className="text-base font-semibold truncate pr-8" title={doc.name}>{doc.name}</DialogTitle>
          <DialogDescription className="sr-only">Pré-visualização do documento</DialogDescription>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="text-[10px]">{doc.category_label}</Badge>
            <span className="text-[11px] text-muted-foreground">{fmtDate(doc.created_at)}</span>
            {doc.size ? <span className="text-[11px] text-muted-foreground">· {fmtSize(doc.size)}</span> : null}
          </div>
        </DialogHeader>

        <div className="bg-muted/30 h-[70vh] relative">
          {loading && (
            <div className="absolute inset-0 grid place-items-center">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
              </div>
            </div>
          )}
          {!loading && error && (
            <div className="absolute inset-0 grid place-items-center px-6 text-center">
              <p className="text-sm text-rose-600">{error}</p>
            </div>
          )}
          {!loading && !error && blobUrl && isPdf && (
            <iframe title={doc.name} src={blobUrl} className="w-full h-full bg-white" data-testid="proc-doc-viewer-pdf" />
          )}
          {!loading && !error && blobUrl && isImg && (
            <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
              <img src={blobUrl} alt={doc.name} className="max-w-full max-h-full object-contain shadow-sm rounded" data-testid="proc-doc-viewer-image" />
            </div>
          )}
          {!loading && !error && blobUrl && !isPdf && !isImg && (
            <div className="absolute inset-0 grid place-items-center px-6 text-center">
              <div>
                <FileIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">Este tipo de arquivo não pode ser pré-visualizado.</p>
                <Button onClick={download}><Download className="h-4 w-4 mr-1" /> Baixar arquivo</Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-white">
          <Button variant="outline" size="sm" onClick={openInNewTab} disabled={!blobUrl || !!error}>
            <ExternalLink className="h-4 w-4 mr-1" /> Abrir em nova aba
          </Button>
          <Button size="sm" onClick={download}>
            <Download className="h-4 w-4 mr-1" /> Baixar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- DELETE DIALOG --------------------
function DeleteDialog({ doc, open, onCancel, onConfirm }) {
  if (!doc) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="proc-doc-delete-dialog">
        <DialogHeader>
          <DialogTitle>Excluir documento</DialogTitle>
          <DialogDescription>Esta ação removerá permanentemente o documento anexado a este processo.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
          <p className="font-medium truncate">{doc.name}</p>
          <p className="text-xs text-muted-foreground">{doc.category_label} · {fmtSize(doc.size)}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="proc-doc-delete-confirm">
            <Trash2 className="h-4 w-4 mr-1" /> Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
